import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ApiVersions } from './api-version';
import { TeamFormDetail } from '../models/team-form/teamFormDetail';
import { TeamFormSummary } from '../models/team-form/teamFormSummary';
import { TeamFormUpdate } from '../models/team-form/teamFormUpdate';
import { BaseService } from './base.service';

@Injectable({
  providedIn: 'root',
})
export class TeamFormService extends BaseService {
  private _teamForm = new BehaviorSubject<TeamFormDetail>(null);
  public readonly teamForm: Observable<TeamFormDetail> = this._teamForm.asObservable();

  constructor() {
    super();
  }

  getSeatsSummary(teamId: string) {
    return this.get(`${this.getBasePathWithTeam(teamId)}/seats-summary`);
  }

  getTeamFormById(id: string): void {
    this.get<TeamFormDetail>(`${this.getBasePath()}/${id}`)
      .pipe(
        map((response) => {
          if (response.deadline !== null) {
            response.deadline = new Date(response.deadline);
          }

          return response;
        })
      )
      .subscribe((data) => {
        if (data) {
          const teamFormDetail = new TeamFormDetail(data);
          this._teamForm.next(teamFormDetail);
        }
      });
  }

  postTeamForm(teamFormUpdate: TeamFormUpdate) {
    let body = JSON.stringify(teamFormUpdate);
    return this.post(this.getBasePath(), body, null, ApiVersions.v0_1, true).pipe(
      tap((response: TeamFormDetail) => {
        const teamForm = new TeamFormDetail(response);
        this._teamForm.next(teamForm);
      })
    );
  }

  putTeamForm(teamFormId: string, teamFormUpdate: TeamFormUpdate) {
    return this.put(
      `${this.getBasePath()}/${teamFormId}`,
      JSON.stringify(teamFormUpdate),
      null,
      ApiVersions.v0_1,
      true
    ).pipe(
      tap((response: TeamFormDetail) => {
        if (response) {
          const teamFormDetail = new TeamFormDetail(response);
          this._teamForm.next(teamFormDetail);
        }
      })
    );
  }

  getAllForTeam(teamId: string): Observable<TeamFormSummary[]> {
    return this.get(`${this.getBasePathWithTeam(teamId)}`).pipe(
      map(
        (data) =>
          data &&
          data.map((teamFormSummary: TeamFormSummary) => TeamFormSummary.parse(teamFormSummary))
      )
    );
  }

  deleteTeamForm(teamFormId: string) {
    return this.delete(`${this.getBasePath()}/${teamFormId}`);
  }

  private getBasePath(): string {
    return `team-forms`;
  }

  private getBasePathWithTeam(teamId: string): string {
    return `team-forms/teams/${teamId}`;
  }
}
